import { Flex, Text } from '@chakra-ui/react'
import React from 'react'

import { textStyleKeys, textStyles } from '@mangovoice/design-system/textStyles'
import type { ExtensionComparisonDirection } from '@mangovoice/types'

import ExportCsvButton from '../components/ExportCsvButton'
import type { ExtensionNameMap } from '../hooks/useExtensionNames'
import { useReportingI18n } from '../i18n'
import { DateRange } from '../shared/DateRange'
import { DateTimeRangeDropdown } from '../shared/DateTimeRangeDropdown'
import { DropdownMangoButton } from '../shared/dropdown/DropdownMangoButton'
import type { MangoButtonMenuItem } from '../shared/dropdown/DropdownMangoButton'
import { MenuPlacement } from '../shared/dropdown/Menu'
import { ExtensionMultiDropdown } from '../shared/ExtensionMultiDropdown'
import { MangoButtonVariant } from '../shared/styles/MangoButton.style'

export interface ExtensionComparisonFiltersProps {
  dateTimeRange?: DateRange
  onValueChange: (newRange: DateRange) => void
  direction: ExtensionComparisonDirection
  onDirectionChange: (direction: ExtensionComparisonDirection) => void
  selectedExtensions?: string[]
  extensions?: string[]
  extensionNameMap?: ExtensionNameMap
  isExtensionsLoading?: boolean
  onExtensionChange?: (extensions: string[]) => void
  onExportCsv?: () => void
  isExportCsvDisabled?: boolean
}

interface DirectionOption {
  value: ExtensionComparisonDirection
  contentText: string
  buttonLabel: string
}

const DIRECTION_OPTIONS: DirectionOption[] = [
  {
    value: 'all',
    contentText: 'Inbound & Outbound',
    buttonLabel: 'Inbound & Outbound',
  },
  { value: 'inbound', contentText: 'Inbound', buttonLabel: 'Inbound' },
  { value: 'outbound', contentText: 'Outbound', buttonLabel: 'Outbound' },
]

export const ExtensionComparisonFilters = ({
  dateTimeRange,
  onValueChange,
  direction,
  onDirectionChange,
  selectedExtensions,
  extensions,
  extensionNameMap,
  isExtensionsLoading,
  onExtensionChange,
  onExportCsv,
  isExportCsvDisabled = true,
}: ExtensionComparisonFiltersProps) => {
  const { text } = useReportingI18n()
  const resolvedExtensions = extensions ?? []
  const resolvedSelected = selectedExtensions ?? []
  const resolvedLoading = isExtensionsLoading ?? false

  const directionItems: MangoButtonMenuItem<ExtensionComparisonDirection>[] =
    DIRECTION_OPTIONS.map((opt) => ({
      value: opt.value,
      content: text(
        `extensionComparisonFilters.direction.${opt.value}`,
        opt.contentText,
      ),
      buttonLabel: text(
        `extensionComparisonFilters.direction.${opt.value}`,
        opt.buttonLabel,
      ),
    }))

  const selectedDirectionIndex = DIRECTION_OPTIONS.findIndex(
    (opt) => opt.value === direction,
  )

  return (
    <Flex
      width="calc(100% + 2.5rem)"
      marginLeft="-1.25rem"
      marginRight="-1.25rem"
      marginTop="-1.25rem"
      flexDirection="column"
      background="bg.default"
    >
      <Flex
        padding="0.75rem 1.25rem"
        flexDirection="column"
        borderBottom="1px solid var(--chakra-colors-border-default)"
      >
        <Text fontSize="1.25rem" fontWeight="800" color="fg.default">
          {text('extensionComparisonFilters.title', 'Extension Comparison')}
        </Text>
        <Text {...textStyles[textStyleKeys.Body2]} color="fg.subtle">
          {text(
            'extensionComparisonFilters.subtitle',
            'Compare call volume across extensions',
          )}
        </Text>
      </Flex>
      <Flex
        padding="0.75rem 1.25rem"
        alignItems="flex-end"
        gap="1.5rem"
        borderBottom="2px solid var(--chakra-colors-border-default)"
      >
        <Flex flexDirection="column" gap="0.25rem">
          <Text {...textStyles[textStyleKeys.Body3]} color="fg.muted">
            {text('reportingFilters.dateRange', 'Date Range')}
          </Text>
          <DateTimeRangeDropdown
            onValueChange={onValueChange}
            dateTimeRange={dateTimeRange}
            useExtendedDates={true}
            menuPlacement={MenuPlacement.BottomLeft}
          />
        </Flex>
        <Flex flexDirection="column" gap="0.25rem">
          <Text {...textStyles[textStyleKeys.Body3]} color="fg.muted">
            {text('extensionComparisonFilters.directionLabel', 'Direction')}
          </Text>
          <DropdownMangoButton
            variant={MangoButtonVariant.Default_200}
            label={
              directionItems[selectedDirectionIndex]?.buttonLabel ??
              text(
                'extensionComparisonFilters.direction.all',
                'Inbound & Outbound',
              )
            }
            items={directionItems}
            selectedIndex={selectedDirectionIndex}
            onSelect={(value) => onDirectionChange(value)}
            menuOptions={{
              width: '200px',
              placement: MenuPlacement.BottomLeft,
            }}
          />
        </Flex>
        <Flex flexDirection="column" gap="0.25rem">
          <Text {...textStyles[textStyleKeys.Body3]} color="fg.muted">
            {text('extensionComparisonFilters.extensionsLabel', 'Extensions')}
          </Text>
          <ExtensionMultiDropdown
            selectedExtensions={resolvedSelected}
            extensions={resolvedExtensions}
            extensionNameMap={extensionNameMap}
            isExtensionsLoading={resolvedLoading}
            onSelectionChange={onExtensionChange}
          />
        </Flex>
        <ExportCsvButton onClick={onExportCsv} disabled={isExportCsvDisabled} />
      </Flex>
    </Flex>
  )
}
